import { Key } from "../enums";
import type { AnalyticsData } from "../../types/data";

export enum SortDirection {
  ASC = "ASC",
  DESC = "DESC",
}

export interface SortConfig<T> {
  key?: keyof T;
  accessor?: (item: T) => string | number | null | undefined;
  direction?: SortDirection;
}

export interface SortPreset {
  id: string;
  label: string;
  key: Key;
  direction: SortDirection;
  accessor: (item: AnalyticsData) => string | number | null | undefined;
}

export const SORT_PRESETS: Partial<Record<Key, SortPreset[]>> = {
  [Key.STUDENT_NAME]: [
    {
      id: "student-name-asc",
      label: "Name (A–Z)",
      key: Key.STUDENT_NAME,
      direction: SortDirection.ASC,
      accessor: (r) => r[Key.STUDENT_NAME].value,
    },
    {
      id: "student-name-desc",
      label: "Name (Z–A)",
      key: Key.STUDENT_NAME,
      direction: SortDirection.DESC,
      accessor: (r) => r[Key.STUDENT_NAME].value,
    },
  ],
  [Key.LESSON_NAME]: [
    {
      id: "lesson-name-asc",
      label: "Lesson (A–Z)",
      key: Key.LESSON_NAME,
      direction: SortDirection.ASC,
      accessor: (r) => r[Key.LESSON_NAME].value,
    },
    {
      id: "lesson-name-desc",
      label: "Lesson (Z–A)",
      key: Key.LESSON_NAME,
      direction: SortDirection.DESC,
      accessor: (r) => r[Key.LESSON_NAME].value,
    },
  ],
  [Key.CLASSROOM_NAME]: [
    {
      id: "classroom-name-asc",
      label: "Group (A–Z)",
      key: Key.CLASSROOM_NAME,
      direction: SortDirection.ASC,
      accessor: (r) => r[Key.CLASSROOM_NAME].value,
    },
    {
      id: "classroom-name-desc",
      label: "Group (Z–A)",
      key: Key.CLASSROOM_NAME,
      direction: SortDirection.DESC,
      accessor: (r) => r[Key.CLASSROOM_NAME].value,
    },
  ],
  [Key.ASSIGN_DATE]: [
    {
      id: "assign-date-asc",
      label: "Date (oldest first)",
      key: Key.ASSIGN_DATE,
      direction: SortDirection.ASC,
      accessor: (r) => r[Key.ASSIGN_DATE],
    },
    {
      id: "assign-date-desc",
      label: "Date (newest first)",
      key: Key.ASSIGN_DATE,
      direction: SortDirection.DESC,
      accessor: (r) => r[Key.ASSIGN_DATE],
    },
  ],
  [Key.TIME_SPENT]: [
    {
      id: "time-spent-asc",
      label: "Time (shortest first)",
      key: Key.TIME_SPENT,
      direction: SortDirection.ASC,
      accessor: (r) => r[Key.TIME_SPENT],
    },
    {
      id: "time-spent-desc",
      label: "Time (longest first)",
      key: Key.TIME_SPENT,
      direction: SortDirection.DESC,
      accessor: (r) => r[Key.TIME_SPENT],
    },
  ],
  [Key.PRE_ASSESSMENT]: [
    {
      id: "pre-assessment-asc",
      label: "Status (asc)",
      key: Key.PRE_ASSESSMENT,
      direction: SortDirection.ASC,
      accessor: (r) => {
        const rank: Record<string, number> = {
          mastered: 5,
          partially_mastered: 4,
          not_mastered: 3,
          not_submitted: 2,
          not_started: 1,
          na: 0,
        };
        return rank[r[Key.PRE_ASSESSMENT].status] ?? -1;
      },
    },
    {
      id: "pre-assessment-desc",
      label: "Status (desc)",
      key: Key.PRE_ASSESSMENT,
      direction: SortDirection.DESC,
      accessor: (r) => {
        const rank: Record<string, number> = {
          mastered: 5,
          partially_mastered: 4,
          not_mastered: 3,
          not_submitted: 2,
          not_started: 1,
          na: 0,
        };
        return rank[r[Key.PRE_ASSESSMENT].status] ?? -1;
      },
    },
  ],
  [Key.EXIT_TICKET]: [
    {
      id: "exit-ticket-asc",
      label: "Status (asc)",
      key: Key.EXIT_TICKET,
      direction: SortDirection.ASC,
      accessor: (r) => {
        const rank: Record<string, number> = {
          mastered: 5,
          partially_mastered: 4,
          not_mastered: 3,
          not_submitted: 2,
          not_started: 1,
          na: 0,
        };
        return rank[r[Key.EXIT_TICKET].status] ?? -1;
      },
    },
    {
      id: "exit-ticket-desc",
      label: "Status (desc)",
      key: Key.EXIT_TICKET,
      direction: SortDirection.DESC,
      accessor: (r) => {
        const rank: Record<string, number> = {
          mastered: 5,
          partially_mastered: 4,
          not_mastered: 3,
          not_submitted: 2,
          not_started: 1,
          na: 0,
        };
        return rank[r[Key.EXIT_TICKET].status] ?? -1;
      },
    },
  ],
  [Key.GOALS]: [
    {
      id: "goals-asc",
      label: "Completion (asc)",
      key: Key.GOALS,
      direction: SortDirection.ASC,
      accessor: (r) => {
        const { goalsMet, totalGoals } = r;
        // A lesson with no bites has no completion ratio; sorting it as 0
        // interleaves it with genuinely 0%-complete lessons, the conflation
        // the "No goals" filter bucket exists to undo. Park it past 100%.
        return totalGoals === 0 ? Infinity : goalsMet / totalGoals;
      },
    },
    {
      id: "goals-desc",
      label: "Completion (dec)",
      key: Key.GOALS,
      direction: SortDirection.DESC,
      accessor: (r) => {
        const { goalsMet, totalGoals } = r;
        // A lesson with no bites has no completion ratio; sorting it as 0
        // interleaves it with genuinely 0%-complete lessons, the conflation
        // the "No goals" filter bucket exists to undo. Park it past 100%.
        return totalGoals === 0 ? Infinity : goalsMet / totalGoals;
      },
    },
  ],
};

/**
 * The columns whose header carries a sort button, as before the reskin. The
 * lesson and group presets stay reachable through the default sort only.
 */
export const COLUMN_SORT_KEYS: ReadonlySet<Key> = new Set([
  Key.STUDENT_NAME,
  Key.PRE_ASSESSMENT,
  Key.EXIT_TICKET,
  Key.GOALS,
  Key.TIME_SPENT,
  Key.ASSIGN_DATE,
]);

export const getDefaultSortPreset = (groupBy: Key): SortPreset | null => {
  const presets = SORT_PRESETS[groupBy];
  if (!presets || presets.length === 0) return null;
  return presets[0];
};
